import axios from "axios";

// Vazio/ausente = mesma origem: os pedidos vão para `/api` no próprio domínio do
// frontend, cujo servidor (server.js) os encaminha para o backend. É isto que
// mantém o cookie de sessão first-party. Um URL absoluto continua a funcionar
// para desenvolvimento local contra um backend noutra porta.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;

let accessToken = null;
let refreshPromise = null;
// AUD-002 — depois de um refresh definitivamente falhado (refresh token
// inválido/expirado), nenhum pedido volta a tentar refresh sozinho até
// existir um novo token válido (login ou bootstrap com sucesso). Sem isto,
// cada pedido subsequente que apanhasse 401 disparava um novo POST
// /auth/refresh contra um refresh token já sabido inválido, indefinidamente.
let refreshDisabled = false;
// AUD-002 — handler registado pelo AuthContext (única fonte de verdade do
// estado de sessão) para reagir a uma falha DEFINITIVA de refresh a meio de
// sessão. api.js não conhece React state; só notifica.
let sessionExpiredHandler = null;
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => {
    try {
      fn(accessToken);
    } catch (e) {
      /* noop */
    }
  });
}

export function setAccessToken(token) {
  accessToken = token || null;
  // Um token válido novo (login, bootstrap ou refresh com sucesso) reabilita
  // a capacidade de voltar a tentar refresh no futuro.
  if (accessToken) refreshDisabled = false;
  notify();
}

export function getAccessToken() {
  return accessToken;
}

export function subscribeAccessToken(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// AUD-002 — o AuthContext regista aqui a sua função de "terminar sessão de
// forma limpa" (limpar user/status + cache React Query). Chamada no máximo
// uma vez por ciclo de refresh falhado, mesmo com vários 401 concorrentes —
// ver comentário em startRefresh().
export function setSessionExpiredHandler(fn) {
  sessionExpiredHandler = fn;
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // send/refresh cookie
});

api.interceptors.request.use((config) => {
  if (accessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

export function isDefinitiveRefreshFailure(err) {
  const status = err?.response?.status;
  return status === 401 || status === 403;
}

// AUD-002 — side-effects de uma falha de refresh (limpar token + avisar o
// AuthContext) vivem AQUI, presos à cadeia da própria promise partilhada, não
// em cada consumidor que a aguarda. `.catch()` de uma promise corre uma única
// vez independentemente de quantos `await refreshPromise` existirem em
// paralelo — é isto que garante que N pedidos com 401 simultâneos resultam em
// UM único refresh, UMA única limpeza de sessão, sem múltiplos
// logouts/redirects concorrentes.
function startRefresh() {
  return axios
    .post(`${API_BASE}/auth/refresh`, null, { withCredentials: true })
    .then((res) => {
      setAccessToken(res.data.access_token);
      return res.data.access_token;
    })
    .catch((err) => {
      // Só 401/403 dizem que a sessão acabou. Sem rede, timeout ou 5xx
      // (deploy, servidor a reiniciar) a sessão continua: o pedido falha,
      // mas o próximo volta a tentar o refresh.
      if (isDefinitiveRefreshFailure(err)) {
        setAccessToken(null);
        refreshDisabled = true;
        if (sessionExpiredHandler) sessionExpiredHandler();
      }
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    if (
      status === 401 &&
      !original._retry &&
      !refreshDisabled &&
      !original.url?.includes("/auth/login") &&
      !original.url?.includes("/auth/refresh")
    ) {
      original._retry = true;
      try {
        if (!refreshPromise) refreshPromise = startRefresh();
        const token = await refreshPromise;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      } catch (e) {
        // Sessão já foi limpa dentro de startRefresh(); aqui só se garante
        // que o pedido original termina (rejeitado), nunca fica pendente.
      }
    }
    return Promise.reject(error);
  },
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Ocorreu um erro. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
